import { useAuth0 } from "@auth0/auth0-react";
import { useSnackbar } from "notistack";
import { useState, forwardRef, useImperativeHandle } from "react";
import { ReactSVG } from "react-svg";
import { useContextState } from "../../context/ContextProvider";
import { submitSupport } from "../../utils/support";
import "./support.scss";

const Support = forwardRef(({ showTrigger = true }, ref) => {
  const [supportOpened, setSupportOpened] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { collapseSideBar } = useContextState();
  const [comment, setComment] = useState("");
  const [categories, setCategories] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Organization context for support requests from specific organizations
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationName, setOrganizationName] = useState("");

  const { getIdTokenClaims } = useAuth0();

  // Expose method to open support panel with organization context
  useImperativeHandle(ref, () => ({
    openWithOrganization: (orgId, orgName) => {
      setOrganizationId(orgId);
      setOrganizationName(orgName);
      setSupportOpened(true);
    }
  }));

  const toggleCategory = (cat) => {
    if (categories.includes(cat)) {
      setCategories(categories.filter((c) => c !== cat));
    } else {
      setCategories([...categories, cat]);
    }
  };

  const onSubmit = async () => {
    // Validate required fields
    if (categories.length === 0) {
      setErrorMessage("Please select at least one category");
      return;
    }

    if (!comment.trim()) {
      setErrorMessage("Please describe your issue or question");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const token = await getIdTokenClaims();

      submitSupport(token.__raw, categories, comment, organizationId)
        .then((res) => {
          if (res.data.is_success) {
            setSupportOpened(false);
            setErrorMessage("");
            setCategories([]);
            setComment("");
            setOrganizationId(null);
            setOrganizationName("");

            enqueueSnackbar("Support request submitted", {
              variant: "success",
            });
          } else {
            setErrorMessage(res.data.error);
          }
          setIsSubmitting(false);
        })
        .catch((err) => {
          enqueueSnackbar("Support request couldn't be submitted, something went wrong", {
            variant: "error",
          });
          console.error(err);
          setIsSubmitting(false);
        });
    } catch (e) {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {showTrigger && (
        <div
          onClick={() => setSupportOpened(!supportOpened)}
          className={
            "flex mb-1 group items-center gap-3 px-3 p-2 rounded-lg hover:bg-blue-500 hover:text-white text-gray-400 cursor-pointer "
          }
        >
          <ReactSVG src={"/assets/icons/support.svg"}/>
          {collapseSideBar ? "" : "Support"}
        </div>
      )}

      <div
        className={`w-[25rem] shadow-lg fixed transition right-4 z-40 bg-white rounded-2xl border border-gray-300 ${
          supportOpened ? "support-animate-in" : "support-animate-out"
        }`}
      >
        <div
          className={
            "flex p-4 text-black items-center border-b border-gray-300 justify-between"
          }
        >
          <p className={"text-xl"}>Support</p>
          <ReactSVG
            onClick={() => setSupportOpened(!supportOpened)}
            src={"/assets/icons/cancel.svg"}
            className={"w-6 text-gray-500 cursor-pointer"}
          />
        </div>
        <div className={"px-6 py-3 border-b border-gray-300 mb-4"}>
          <p className={"text-base mb-3"}>
            How can we help you?
          </p>
          {organizationName && (
            <div className={"mb-4"}>
              <p className={"text-sm font-light mb-1 text-gray-600"}>Organization</p>
              <input
                type="text"
                className={
                  "w-full outline-none p-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                }
                value={organizationName}
                disabled
                readOnly
              />
            </div>
          )}
          <div className={"mt-3"}>
            <p className={"text-lg font-light mb-2"}>Select Categories (multiple allowed)</p>
            <div
              className={"flex items-start gap-3 mb-4 flex-wrap"}
              id="support_categories"
            >
              <button
                className={`outline-none border border-gray-300 rounded-lg px-2 py-1 font-medium ${
                  categories.includes("backup") ? "selected-category" : ""
                }`}
                onClick={() => {
                  toggleCategory("backup");
                }}
              >
                Backup
              </button>
              <button
                className={`outline-none border border-gray-300 rounded-lg px-2 py-1 font-medium ${
                  categories.includes("restore") ? "selected-category" : ""
                }`}
                onClick={() => {
                  toggleCategory("restore");
                }}
              >
                Restore
              </button>
              <button
                className={`outline-none border border-gray-300 rounded-lg px-2 py-1 font-medium ${
                  categories.includes("archived_files") ? "selected-category" : ""
                }`}
                onClick={() => {
                  toggleCategory("archived_files");
                }}
              >
                Archived Files
              </button>
              <button
                className={`outline-none border border-gray-300 rounded-lg px-2 py-1 font-medium ${
                  categories.includes("ui") ? "selected-category" : ""
                }`}
                onClick={() => {
                  toggleCategory("ui");
                }}
              >
                UI
              </button>
              <button
                className={`outline-none border border-gray-300 rounded-lg px-2 py-1 font-medium ${
                  categories.includes("others") ? "selected-category" : ""
                }`}
                onClick={() => {
                  toggleCategory("others");
                }}
              >
                Others
              </button>
            </div>
            <textarea
              className={
                "w-full outline-none p-3 h-36 border border-gray-300 rounded-xl bg-gray-100"
              }
              placeholder={"Describe your issue or question..."}
              value={comment}
              onChange={(el) => setComment(el.target.value)}
            ></textarea>
          </div>
        </div>
        <div className={"px-4"}>
          <div dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
          <button
            className={
              "bg-blue-600 text-white rounded-xl flex items-center justify-center py-2 mb-6 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            }
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
});

Support.displayName = "Support";

export default Support;
